/**
 * Which deck line explains a shot (SPEC_CAPTURE_UX_R2 §7.2 "why this shot") and which §4 caption names a good one —
 * by the prompt-key FAMILY the seeded scripts use (mock/fixtures.ts, the be's templates). Unknown keys fall back to the
 * generic line; nothing is ever fabricated per key.
 */

export type ShotFamily = 'EXT' | 'ENTRANCE' | 'INT' | 'DOOR_PLATE' | 'CERT_WALL' | 'SIGNBOARD' | 'ACTIVITY' | 'INSTALL_POINT' | 'HOME' | 'REP_SELFIE' | 'ID_DOCUMENT' | 'CHALLENGE' | 'generic';

export function familyOf(promptKey: string | null | undefined): ShotFamily {
  const k = (promptKey ?? '').toUpperCase();
  // US-03482 P1-2: the dedicated still of the signboard / GST certificate (any key that names it) — before EXT_.
  if (k.includes('SIGNBOARD') || k.includes('GST_CERT')) return 'SIGNBOARD';
  if (k === 'EXT_ENTRANCE') return 'ENTRANCE';
  if (k.startsWith('EXT_')) return 'EXT';
  if (k.startsWith('HOME_')) return 'HOME';
  if (k.startsWith('INT_')) return 'INT';
  if (k === 'DOOR_PLATE') return 'DOOR_PLATE';
  if (k === 'CERT_WALL') return 'CERT_WALL';
  if (k === 'ACTIVITY') return 'ACTIVITY';
  if (k === 'INSTALL_POINT') return 'INSTALL_POINT';
  if (k === 'REP_SELFIE') return 'REP_SELFIE';
  if (k === 'ID_DOCUMENT' || k === 'REP_ID') return 'ID_DOCUMENT';
  if (k === 'CHALLENGE') return 'CHALLENGE';
  return 'generic';
}

export function whyKeyFor(promptKey: string | null | undefined): string {
  return `sv.help.why.${familyOf(promptKey)}`;
}

/**
 * The caption when there is nothing to demonstrate. Phase 3 made the §4 demonstrations real, so a TAUGHT shot type
 * takes the demonstration's own caption (`demos/demoTypes.ts#demoCaptionKey`) — one caption per demonstration,
 * under the art, never duplicated into a second key that could drift. A prompt key we do not teach keeps this
 * neutral line. (The mapping lives in `demos/`, which imports `familyOf` from here; this file imports nothing back.)
 */
export const GENERIC_CAPTION_KEY = 'sv.help.cap.generic';

/** The `step` token a help-request carries (be: letters, digits, `_ . : -`, ≤ 64) — the prompt key on a capture screen, else the screen. */
export function stepTokenFor(screen: string, promptKey: string | null | undefined): string {
  const raw = promptKey && promptKey.trim() !== '' ? promptKey : screen;
  return raw.replace(/[^A-Za-z0-9_.:-]/g, '_').slice(0, 64);
}

/** The reason picker's closed list (§7.2) — deck keys; the picked reason travels as its key, stays on this device. */
export const SKIP_REASONS = ['reach', 'none', 'unsafe', 'other'] as const;
export type SkipReason = (typeof SKIP_REASONS)[number];
